import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { getCurrentUser, User, adminChangeUserPassword } from '../services/authService';
import { adminService } from '../services/adminService';
import TopBar from '../components/TopBar';
import Button from '../components/Button';
import Input from '../components/Input';
import ConfirmationModal from '../components/ConfirmationModal';
import { APP_NAME } from '../constants';
import { uploadProfilePicture } from '../services/storageService';

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const ProfileSection = styled.div`
  width: 100%;
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: 24px;
  padding: 32px;
  margin-bottom: 24px;
  width: 100%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 30px;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const ProfileImageContainer = styled.div`
  flex-shrink: 0;
`;

const NameContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (min-width: 768px) {
    gap: 20px;
  }
`;

const ProfilePictureSection = styled.label`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  display: block;
  
  &::after {
    content: 'Change Photo';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 12px;
    padding: 5px;
    text-align: center;
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover::after {
    opacity: 1;
  }
  
  input {
    display: none;
  }
`;



const ProfilePicture = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DefaultProfilePicture = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 48px;
  font-weight: 600;
`;


const FormSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 8px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 16px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  margin-bottom: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  accent-color: #381f74;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 16px;
  color: #333;
  cursor: pointer;
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 2px solid #f0f0f0;

  @media (max-width: 600px) {
    justify-content: stretch;
    flex-direction: column-reverse;
  }
`;

const ErrorMessage = styled.div`
  background-color: #f44336;
  color: white;
  padding: 15px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
`;

const SuccessMessage = styled.div`
  background-color: #4caf50;
  color: white;
  padding: 15px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
`;

const LoadingPlaceholder = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
`;

const TextAreaContainer = styled.div`
  margin: 16px 0;
`;

const TextAreaLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #333;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  font-size: 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #f9f9f9;
  font-family: 'Montserrat', sans-serif;
  outline: none;
  color: #333;
  resize: vertical;

  &:focus {
    border-color: #381f74;
    background: white;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const UploadingOverlay = styled.div<{ uploading: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  display: ${props => props.uploading ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
`;

const EditUserPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    emailVerified: false,
    phoneVerified: false,
    profilePictureURL: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const currentUserData = await getCurrentUser();
        
        if (!currentUserData || !adminService.isAdmin(currentUserData)) {
          navigate('/dashboard');
          return;
        }

        setCurrentUser(currentUserData);

        if (userId) {
          // Fetch the user to edit
          const allUsers = await adminService.getAllUsers();
          const user = allUsers.find(u => u.id === userId);
          
          if (!user) {
            setError('User not found');
            return;
          }

          setUserToEdit(user);
          setFormData({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phoneNumber: user.phoneNumber || '',
            emailVerified: user.emailVerified || false,
            phoneVerified: user.phoneVerified || false,
            profilePictureURL: user.profilePictureURL || ''
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, navigate]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    
    // For verification status changes, provide immediate feedback
    if (field === 'emailVerified' || field === 'phoneVerified') {
      const verifiedType = field === 'emailVerified' ? 'Email' : 'Phone';
      const verifiedStatus = value ? 'verified' : 'unverified';
      setSuccess(`${verifiedType} verification status updated to ${verifiedStatus}. Remember to save changes.`);
    } else {
      setSuccess('');
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userId) return;

    try {
      setUploading(true);
      setError('');

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file size must be less than 5MB');
        return;
      }

      const downloadURL = await uploadProfilePicture(userId, file);
      
      setFormData(prev => ({
        ...prev,
        profilePictureURL: downloadURL
      }));

      setSuccess('Profile picture uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!userToEdit) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Validate required fields
      if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
        setError('First name, last name, and email are required.');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Please enter a valid email address.');
        return;
      }

      // Filter out systemRole and role from formData since we don't want to update them from this page
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        emailVerified: formData.emailVerified,
        phoneVerified: formData.phoneVerified,
        profilePictureURL: formData.profilePictureURL
      };

      await adminService.updateUser(userToEdit.id, updateData);
      
      // Check if verification statuses have been changed to provide specific feedback
      const emailVerifiedChanged = formData.emailVerified !== userToEdit.emailVerified;
      const phoneVerifiedChanged = formData.phoneVerified !== userToEdit.phoneVerified;
      
      if (emailVerifiedChanged || phoneVerifiedChanged) {
        let message = 'User updated successfully. ';
        
        if (emailVerifiedChanged) {
          message += `Email ${formData.emailVerified ? 'verified' : 'unverified'}. `;
        }
        
        if (phoneVerifiedChanged) {
          message += `Phone ${formData.phoneVerified ? 'verified' : 'unverified'}.`;
        }
        
        setSuccess(message);
      } else {
        setSuccess('User updated successfully');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      setError('Failed to save user changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/users');
  };

  const handleDeleteUser = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToEdit) return;

    try {
      setDeleting(true);
      setError('');
      setSuccess('');
      
      // Use the new soft delete method with deletion reason
      await adminService.deleteUser(userToEdit.id, deletionReason || undefined);
      
      // Navigate back to users page with success message
      navigate('/admin/users', { 
        state: { 
          message: `User ${userToEdit.firstName} ${userToEdit.lastName} deleted successfully` 
        } 
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user');
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteModal(false);
    setDeletionReason(''); // Clear the deletion reason
  };

  const handleChangePassword = async () => {
    if (!userToEdit) return;

    try {
      setChangingPassword(true);
      setError('');
      setSuccess('');

      // Validate password
      if (!newPassword || newPassword.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }

      if (newPassword !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      await adminChangeUserPassword(userToEdit.id, newPassword);
      
      setSuccess('Password changed successfully');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordSection(false);
    } catch (error) {
      console.error('Error changing password:', error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to change password');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (loading) {
    return (
      <Container>
        <LoadingPlaceholder>Loading user data...</LoadingPlaceholder>
      </Container>
    );
  }

  if (!currentUser || !adminService.isAdmin(currentUser) || !userToEdit) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Edit User</title>
        <meta name="description" content="Edit user information" />
      </Helmet>
      
      <Container>
        <ContentWrapper>
          <TopBar user={currentUser} />
          
          <Breadcrumbs>
            <BreadcrumbLink onClick={() => navigate('/dashboard')}>My Account</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={() => navigate('/admin')}>Admin Dashboard</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={() => navigate('/admin/users')}>Users</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <span>Edit User</span>
          </Breadcrumbs>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
          
          <ProfileSection>
            <PageTitle>Edit User</PageTitle>
            
            <FormContainer>
              <ProfileHeader>
                <ProfileImageContainer>
                  <ProfilePictureSection>
                    {formData.profilePictureURL ? (
                      <ProfilePicture src={formData.profilePictureURL} alt="Profile" />
                    ) : (
                      <DefaultProfilePicture>
                        {getInitials(formData.firstName, formData.lastName)}
                      </DefaultProfilePicture>
                    )}
                    <UploadingOverlay uploading={uploading}>
                      {uploading ? 'Uploading...' : ''}
                    </UploadingOverlay>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                  </ProfilePictureSection>
                </ProfileImageContainer>
                
                <NameContainer>
                  <Input
                    type="text"
                    name="firstName"
                    label="First Name"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="First Name"
                    secondary
                    required
                  />
                  <Input
                    type="text"
                    name="lastName"
                    label="Last Name"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Last Name"
                    secondary
                    required
                  />
                </NameContainer>
              </ProfileHeader>

              <FormSection>
                <SectionTitle>Contact Information</SectionTitle>
                
                <FormRow>
                  <Input
                    type="email"
                    name="email"
                    label="Email Address"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Email"
                    secondary
                    required
                  />
                </FormRow>

                <FormRow>
                  <Input
                    type="tel"
                    name="phoneNumber"
                    label="Phone Number"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    placeholder="Phone Number"
                    secondary
                  />
                </FormRow>
              </FormSection>

              <FormSection>
                <SectionTitle>Verification Status</SectionTitle>
                
                <CheckboxContainer>
                  <Checkbox
                    id="emailVerified"
                    type="checkbox"
                    checked={formData.emailVerified}
                    onChange={(e) => handleInputChange('emailVerified', e.target.checked)}
                  />
                  <CheckboxLabel htmlFor="emailVerified">Email Verified</CheckboxLabel>
                </CheckboxContainer>

                <CheckboxContainer>
                  <Checkbox
                    id="phoneVerified"
                    type="checkbox"
                    checked={formData.phoneVerified}
                    onChange={(e) => handleInputChange('phoneVerified', e.target.checked)}
                  />
                  <CheckboxLabel htmlFor="phoneVerified">Phone Verified</CheckboxLabel>
                </CheckboxContainer>
              </FormSection>

              <FormSection>
                <SectionTitle>Password Management</SectionTitle>
                
                {!showPasswordSection ? (
                  <div style={{
                    padding: '16px',
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px'
                  }}>
                    <div>
                      <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                        Change the user's password directly without requiring verification.
                      </p>
                    </div>
                    <Button
                      text="Change Password"
                      onClick={() => setShowPasswordSection(true)}
                      bgColor="#381f74"
                      textColor="#fff"
                      width="auto"
                      minWidth="150px"
                    />
                  </div>
                ) : (
                  <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px' }}>
                    <FormRow>
                      <Input
                        type="password"
                        name="newPassword"
                        label="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password (minimum 6 characters)"
                        secondary
                        required
                      />
                    </FormRow>
                    <FormRow>
                      <Input
                        type="password"
                        name="confirmPassword"
                        label="Confirm New Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        secondary
                        required
                      />
                    </FormRow>
                    <div style={{ 
                      display: 'flex', 
                      gap: '12px', 
                      justifyContent: 'flex-end',
                      marginTop: '16px'
                    }}>
                      <Button
                        text="Cancel"
                        onClick={() => {
                          setShowPasswordSection(false);
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                        outline={true}
                        bgColor="#666"
                        textColor="#666"
                        width="auto"
                        minWidth="100px"
                      />
                      <Button
                        text={changingPassword ? "Changing..." : "Change Password"}
                        onClick={handleChangePassword}
                        bgColor="#381f74"
                        textColor="#fff"
                        width="auto"
                        minWidth="150px"
                        disabled={changingPassword || !newPassword || !confirmPassword}
                      />
                    </div>
                  </div>
                )}
              </FormSection>

              <ButtonContainer>
                <Button
                  text="Cancel"
                  onClick={handleCancel}
                  outline={true}
                  bgColor="#666"
                  textColor="#666"
                  width="auto"
                  minWidth="120px"
                />
                <Button
                  text={saving ? "Saving..." : "Save"}
                  onClick={handleSubmit}
                  bgColor="#000"
                  textColor="#fff"
                  width="auto"
                  minWidth="120px"
                  disabled={saving || uploading}
                />
              </ButtonContainer>

              {/* Danger Zone */}
              <FormSection style={{ marginTop: '40px', paddingTop: '24px' }}>
                <div style={{ 
                  background: '#ffebee', 
                  padding: '20px', 
                  borderRadius: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '16px'
                }}>
                  <div>
                    <h4 style={{ margin: '0 0 8px 0', color: '#d32f2f', fontWeight: '600' }}>
                      Delete User Account
                    </h4>
                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                      Permanently delete this user account. This action cannot be undone.
                    </p>
                  </div>
                  <Button
                    text="Delete"
                    onClick={handleDeleteUser}
                    bgColor="#d32f2f"
                    textColor="#fff"
                    width="auto"
                    minWidth="120px"
                  />
                </div>
              </FormSection>
          </FormContainer>
        </ProfileSection>
        </ContentWrapper>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          title="Delete User"
          message={
            <div>
              <p style={{ marginBottom: '16px' }}>
                {userToEdit ? 
                  `Are you sure you want to permanently delete ${userToEdit.firstName} ${userToEdit.lastName}? This action cannot be undone and will remove all associated data including transactions, staff entries, store credit, and credit activities.` :
                  'Are you sure you want to delete this user?'
                }
              </p>
              <TextAreaContainer>
                <TextAreaLabel htmlFor="deletionReason">
                  Deletion Reason (Optional)
                </TextAreaLabel>
                <StyledTextArea
                  id="deletionReason"
                  value={deletionReason}
                  onChange={(e) => setDeletionReason(e.target.value)}
                  placeholder="Enter the reason for deleting this user account..."
                  rows={3}
                />
              </TextAreaContainer>
            </div>
          }
          confirmText="Delete User"
          cancelText="Cancel"
          onConfirm={confirmDeleteUser}
          onCancel={cancelDeleteUser}
          isConfirming={deleting}
          confirmButtonVariant="danger"
        />
      </Container>
    </>
  );
};

export default EditUserPage;
